import { Injectable } from '@angular/core';
import { CartService, Product, ProductService } from 'gung-standard';
import { ReplaySubject, Subject, first, forkJoin, mergeMap, of, switchMap, takeUntil } from 'rxjs';

export interface CarLinerBrochuresHelper {
  quantity: number;
  maxQuantity: number;
  products: Product[];
  decision: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CarLinerBrochuresHelperService {
  unsubscribe: Subject<boolean> = new Subject<boolean>();
  artvToInclude = ['8050', '7030'];
  productIdSubject: Subject<string>;

  blockAddProductSubject: Subject<string[]>;

  private cachedData: { [id: string]: Product; } = {};
  private brochures: {[id: string]: CarLinerBrochuresHelper} = {};
  brochuresDecision: {[id: string]: boolean} = {};

  constructor(
    protected cartService: CartService,
    protected productService: ProductService
  ) { }

  public initCheckCartBrouchures() {
    this.cartService.getCurrentCart().pipe(
      takeUntil(this.unsubscribe),
      switchMap(currentCart => {
        const ids = currentCart.map(c => c.productId);
        const cachedIds = Object.keys(this.cachedData);
        const toGetIds = ids.filter(id => !cachedIds.includes(id));

        return forkJoin({
          currentCart: of(currentCart),
          products: this.productService.getProductsByIdsUnfiltered(toGetIds).pipe(first())
        })
      }),
      mergeMap(({currentCart, products}) => {
        this.cachedData = {
          ...this.cachedData,
          ...products.reduce((prev, curr) => ({...prev, [curr.id]: curr}), {})
        }
        const ids = [];
        for (const cartRow of currentCart) {
          const product = this.cachedData[cartRow.productId];
          if (product.extra.artv?.length > 0) {
            const notCached = product.extra.artv.map(artv => artv.artnrtillvalart).filter(id => !this.cachedData[id]);
            ids.push(...notCached);
          }
        }
        return forkJoin({
          currentCart: of(currentCart),
          products: ids.length > 0 ? this.productService.getFullProductsByIds(ids) : of([])
        })
      })
    ).subscribe(({currentCart, products}) => {
      this.brochures = {};
      this.cachedData = {
        ...this.cachedData,
        ...products.reduce((prev, curr) => ({...prev, [curr.id]: curr}), {})
      }
      for (const cartRow of currentCart) {
        const product = this.cachedData[cartRow.productId];
        if (cartRow.productPartialId?.startsWith('artv_')) {
          const productPartialId = cartRow.productPartialId.slice('artv_'.length);
          if (!this.brochures[productPartialId] ) {
            this.brochures[productPartialId] = {
              quantity: this.brochures[productPartialId]?.quantity || 0,
              products: this.brochures[productPartialId]?.products || [],
              maxQuantity: 0,
              decision: this.brochuresDecision[productPartialId] || false
            };
          }
          this.brochures[productPartialId].quantity += cartRow.qty;
        } else if (product.extra.artv?.length > 0) {
          if (!this.brochures[product.id] ) {
            this.brochures[product.id] = {
              quantity: this.brochures[product.id]?.quantity || 0,
              products: this.brochures[product.id]?.products || [],
              maxQuantity: 0,
              decision: this.brochuresDecision[product.id] || false
            };
          }
          this.brochures[product.id].maxQuantity = cartRow.qty;
          for (const artv of product.extra.artv) {
            if (this.artvToInclude.includes(this.cachedData[artv.artnrtillvalart]?.extra?.ar?.varugruppkod)) {
              this.brochures[product.id].products.push(this.cachedData[artv.artnrtillvalart]);
            }
          }
        }
      }

      // Check if some brochures quantity > product quantity
      for (const key of Object.keys(this.brochures)) {
        const brochures = this.brochures[key];
        if (brochures.products.length < 1) {
          this.brochures[key].decision = this.brochuresDecision[key] = true;
        }
        if (brochures.quantity > 0) {
          delete this.brochuresDecision[key];
        }
        this.brochures[key].decision = brochures.quantity > 0 || this.brochuresDecision[key] || false;
      }
    })
  }

  public closeCheckCartBrouchures() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  getBrochuresById(id: string) {
    return this.brochures[id];
  }

  setBrochuresDecision(id: string, decision: boolean) {
    this.brochures[id].decision = this.brochuresDecision[id] = decision;
  }

  setProductId(id: string){
    if (!this.productIdSubject) {
      this.productIdSubject = new ReplaySubject<string>(1);
    }
    this.productIdSubject.next(id);
  }

  getProductId() {
    if (!this.productIdSubject) {
      this.productIdSubject = new ReplaySubject<string>(1);
    }
    return this.productIdSubject.asObservable();
  }

  getMaxQty(productId) {
    return this.brochures[productId].maxQuantity;
  }

  setBlockAddProduct(blocked: string[]){
    if (!this.blockAddProductSubject) {
      this.blockAddProductSubject = new ReplaySubject<string[]>(1);
    }
    this.blockAddProductSubject.next(blocked);
  }

  getBlockAddProduct() {
    if (!this.blockAddProductSubject) {
      this.blockAddProductSubject = new ReplaySubject<string[]>(1);
    }
    return this.blockAddProductSubject.asObservable();
  }

  clearHelper() {
    if (this.productIdSubject) {
      // this.productIdSubject.next(null);
      this.productIdSubject.complete();
      this.productIdSubject = undefined;
    }
    if (this.blockAddProductSubject) {
      // this.blockAddProductSubject.next(null);
      this.blockAddProductSubject.complete();
      this.blockAddProductSubject = undefined;
    }
  }
}
